import moment from "moment";
import { FoaPart, ProcedureType, Regulation } from "../../../../../autogen/bff-api";
import { isDesignContest, isDpsCompetition, isDpsScheme, isTwoStageProcedure } from "./procedure";

// Client mirror of constants the BFF owns; a drift here misleads the buyer without failing anything,
// since the BFF still rejects a bad deadline. Sources:
// bff/domain/.../sourcingevent/v2/Standstill.kt
// bff/domain/.../sourcingevent/v2/ProcedureRules.kt
// bff/domain/.../sourcingevent/v2/publicprocurement/ProcurementThresholds.kt (the part pair below)
const EEA_TIERS: Record<"FOA" | "UTILITIES", { ordinary: FoaPart; specialServices: FoaPart }> = {
  FOA: { ordinary: FoaPart.PartIii, specialServices: FoaPart.PartIv },
  UTILITIES: { ordinary: FoaPart.PartIi, specialServices: FoaPart.PartIii },
};

const tiersFor = (regulation: Regulation | null | undefined) =>
  regulation === Regulation.Forsyningsforskriften ? EEA_TIERS.UTILITIES : EEA_TIERS.FOA;

export const isAtOrAboveEea = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
): boolean => {
  const tiers = tiersFor(regulation);
  return foaPart === tiers.ordinary || foaPart === tiers.specialServices;
};

export const isFoaNationalTier = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
): boolean => (regulation ?? Regulation.Foa) === Regulation.Foa && foaPart === FoaPart.PartIi;

export const isOrdinaryEeaTier = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
): boolean => foaPart === tiersFor(regulation).ordinary;

export const autoKarensDays = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType?: ProcedureType | null,
): number | null => {
  if (isDpsScheme(procedureType) || isDpsCompetition(procedureType) || isDesignContest(procedureType)) return null;
  if (procedureType === "NEGOTIATED_WITHOUT_PRIOR_CALL") return null;
  return isAtOrAboveEea(regulation, foaPart) ? 10 : null;
};

// FOA § 20-3 (25 or 30 days) and § 26-7 (10); forsyningsforskriften § 16-2 (30) and § 16-3 (10).
// FOA's del IV is over the threshold but on the light regime, so only del III carries fixed numbers.
export const minTenderDays = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType?: ProcedureType | null,
): number | null => {
  if (!isOrdinaryEeaTier(regulation, foaPart)) return null;
  if (isDpsScheme(procedureType)) return null;
  if (isDpsCompetition(procedureType)) return 10;
  // § 9-2 and § 13-3 publish no notice, so no minimum runs from one.
  if (procedureType === "NEGOTIATED_WITHOUT_PRIOR_CALL") return null;
  if (isDesignContest(procedureType)) return null;
  if (procedureType == null) {
    return regulation === Regulation.Forsyningsforskriften ? null : 30;
  }
  if (regulation === Regulation.Forsyningsforskriften) {
    return isTwoStageProcedure(procedureType) ? 10 : 30;
  }
  return isTwoStageProcedure(procedureType) ? 25 : 30;
};

// An interpolation value, never a translation key: a paragraph reference reads the same in both
// languages, and translating one would invent a citation that does not exist.
export const tenderDeadlineProvision = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType?: ProcedureType | null,
): string | null => {
  if (minTenderDays(regulation, foaPart, procedureType) == null) return null;
  if (regulation !== Regulation.Forsyningsforskriften) return "FOA § 20-3";
  return isTwoStageProcedure(procedureType) ? "forsyningsforskriften § 16-3" : "forsyningsforskriften § 16-2";
};

// FOA § 20-3: 30 days from the notice. Forsyningsforskriften § 16-3 (1): 30 as the general rule,
// never under 15; its dynamic purchasing system keeps 30.
export const minRequestDays = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType: ProcedureType | null | undefined,
): number | null => {
  if (!isOrdinaryEeaTier(regulation, foaPart) || !isTwoStageProcedure(procedureType) || isDesignContest(procedureType)) {
    return null;
  }
  return requestDaysGeneralRule(regulation, foaPart, procedureType) != null ? 15 : 30;
};

export const requestDaysGeneralRule = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType: ProcedureType | null | undefined,
): number | null =>
  regulation === Regulation.Forsyningsforskriften &&
  isOrdinaryEeaTier(regulation, foaPart) &&
  isTwoStageProcedure(procedureType) &&
  !isDesignContest(procedureType) &&
  procedureType !== ProcedureType.DynamicPurchasingSystem &&
  procedureType !== ProcedureType.QualificationScheme
    ? 30
    : null;

// FOA § 16-12 (3): 5 for a restricted tender, 3 for the negotiated family; FOA § 8-9 (3): 3 in Del II.
// Forsyningsforskriften has no verified counterpart, so none is enforced there.
export const minCandidatesWhenCapped = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType: ProcedureType | null | undefined,
): number | null => {
  if (regulation === Regulation.Forsyningsforskriften) return null;
  if (procedureType === ProcedureType.RestrictedTenderCompetition && isFoaNationalTier(regulation, foaPart)) return 3;
  if (!isOrdinaryEeaTier(regulation, foaPart) || !isTwoStageProcedure(procedureType)) return null;
  return procedureType === ProcedureType.Restricted || procedureType === ProcedureType.RestrictedTenderCompetition
    ? 5
    : 3;
};

// Forsyningsforskriften § 17-2 (2) c and § 16-3 (1); mirrors PriorInformationCall and ProcedureRules.
export const PRIOR_INFORMATION_CALL = {
  earliestInvitationDays: 35,
  latestInvitationMonths: 12,
  minRequestDaysAfterInvitation: 15,
  requestDaysGeneralRule: 30,
} as const;

// Mirrors Instant.plus(days), by which the BFF still judges the FOA minimums: whole 24-hour days, so a
// deadline across the clock change on the last Sunday of October is judged as the server judges it.
export const daysAfter = (from: moment.MomentInput, days: number): moment.Moment =>
  moment(from).add(days * 24, "hours");
